// Pure helpers for the Tutors list (047 D36–D41): search, the Status filter, the sort, the
// client-side pages, and every cell's text. No React and no MUI: Jest loads this file.
import { countText } from "./kitPresentation";
import { periodRange } from "./payCycle";
import { egp } from "./queuePresentation";
import { lessonsEarned } from "./teachingHours";
import { toDate } from "./ticketPresentation";
import { getVerifyStatus } from "./verificationPresentation";

export const PAGE_SIZES = [25, 50];

// The Status menu: today's three Verify status choices. The Queue's ?verify=pending link
// presets Pending (024 D7).
export const TUTOR_FILTERS = [
  { key: "all", label: "All" },
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
];

// A tutor's subjects as a list: the app writes an array, an old doc may hold one string.
// Empty entries ("", null) are dropped before they become text (P65).
export const subjectList = (subjects) =>
  (Array.isArray(subjects) ? subjects : subjects ? [subjects] : []).filter(Boolean).map(String);

// A case-insensitive piece of the name, phone, email or a subject. An empty search matches
// every tutor, even one with none of those fields (P44).
export const matchesSearch = (tutor, term = "") => {
  const needle = term.trim().toLowerCase();
  if (!needle) return true;
  return [tutor.name, tutor.phone, tutor.email, subjectList(tutor.subjects).join(", ")]
    .filter(Boolean)
    .some((v) => String(v).toLowerCase().includes(needle));
};

// "pending" and "approved" match the stored status only: a doc without one is reachable
// under All, as the Queue tile counts it (024 D7).
export const matchesFilter = (tutor, filter = "all") => filter === "all" || getVerifyStatus(tutor) === filter;

// The page's sort: text ignores case and a missing name sorts as "" (P45), a missing Owed
// counts as 0, and a missing date goes last in either direction.
const sortValue = (tutor, field) => {
  const value = tutor[field];
  if (field === "createdAt") return toDate(value);
  if (field === "unpaidEarning") return value ?? 0;
  return typeof value === "string" ? value.toLowerCase() : value ?? "";
};

export const sortTutors = (rows, field = "name", direction = "asc") => {
  const sign = direction === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = sortValue(a, field);
    const bv = sortValue(b, field);
    if (field === "createdAt" && (!av || !bv)) return av ? -1 : bv ? 1 : 0;
    if (av < bv) return -sign;
    if (av > bv) return sign;
    return 0;
  });
};

export const visibleTutors = (rows, { term = "", filter = "all", sortField = "name", sortDirection = "asc" } = {}) =>
  sortTutors(
    rows.filter((t) => matchesSearch(t, term) && matchesFilter(t, filter)),
    sortField,
    sortDirection
  );

// One page of a list read in full. A page past the end (after a filter or an edit shrank the
// list) falls back to the last page that has rows.
export const pageSlice = (rows, page, pageSize) => {
  const last = Math.max(0, Math.ceil(rows.length / pageSize) - 1);
  const safe = Math.min(Math.max(page, 0), last);
  return {
    rows: rows.slice(safe * pageSize, (safe + 1) * pageSize),
    page: safe,
    hasMore: (safe + 1) * pageSize < rows.length,
  };
};

// The overview card's three boxes (047 D36): what the page's three cards showed.
export const overviewTiles = ({ total = null, current = null, previous = null } = {}) => [
  { key: "total", value: total === null ? "—" : countText(total), label: "Total tutors" },
  { key: "current", value: current ? periodRange(current) : "—", label: "Current pay cycle" },
  { key: "previous", value: previous ? periodRange(previous) : "—", label: "Previous pay cycle" },
];

// The Status chip (047 D39): the stored word, capitalised; a doc without one reads Pending,
// as it always has. Tones from the chip table (D15): approved green, rejected red, else amber.
export const verifyChip = (tutor) => {
  const status = getVerifyStatus(tutor) || "pending";
  const color = status === "approved" ? "success" : status === "rejected" ? "error" : "warning";
  return { label: status.charAt(0).toUpperCase() + status.slice(1), color };
};

// The Subjects cell (Figma's Chip + "+2"): the first subject, how many more, and the full
// list for the tooltip; null without subjects.
export const subjectChips = (subjects) => {
  const list = subjectList(subjects);
  return list.length ? { first: list[0], more: list.length - 1, all: list.join(", ") } : null;
};

// "Male" / "Female"; anything else is "—" (P46).
export const genderText = (gender) => (gender === "male" ? "Male" : gender === "female" ? "Female" : "—");

// "4.6": one decimal. A tutor with no rating has 0, which no real average can be (P47).
export const ratingText = (rating) => {
  const value = Number(rating);
  return value > 0 ? value.toFixed(1) : "—";
};

// "12.5 hrs": at most two decimals, so a 40-minute lesson cannot print 0.6666666666666666.
export const hoursText = (hours) => `${Math.round((Number(hours) || 0) * 100) / 100} hrs`;

// "E£1,250": what the page owes the tutor. It is the Queue's `egp`, so every E£ amount on the
// dashboard prints alike, and a missing or broken amount reads E£0 (P66).
export const owedText = egp;

// "E£4,980": what a cycle's paid lessons ([{ hours, start }]) earned, each at the rate the tutor
// had when it started (053 D14, amending 047 D86); "—" when the tutor has no rate now and none
// before.
export const earnedText = (lessons, tutor) =>
  Number(tutor?.salary) > 0 || (tutor?.salaryHistory || []).length > 0 ? egp(lessonsEarned(lessons, tutor)) : "—";

// A money field an admin typed (Edit tutor): empty is 0; anything else must be a number from 0
// to `max` (when given), kept to piastres. → { value } or { error } (053 D21).
export const parseMoneyInput = (text, { label, max } = {}) => {
  const raw = String(text ?? "").trim();
  const n = raw === "" ? 0 : Number(raw);
  if (!Number.isFinite(n) || n < 0) return { error: `${label} must be a number of zero or more.` };
  if (max != null && n > max) return { error: `${label} must be at most ${egp(max)}.` };
  return { value: Math.round(n * 100) / 100 };
};

// The empty table (047 D41): the title, a line on what to try, and which clear button to
// offer — "search", "filter", or none. A search names the filter it ran inside.
export const tutorEmptyState = ({ term = "", filter = "all" } = {}) => {
  const f = TUTOR_FILTERS.find((x) => x.key === filter && x.key !== "all");
  const needle = term.trim();
  if (needle) {
    return {
      title: `No ${f ? `${f.label.toLowerCase()} ` : ""}tutor matches "${needle}".`,
      message: "Search looks in names, emails, phone numbers and subjects.",
      clear: "search",
    };
  }
  if (f) return { title: `No tutors match ${f.label}.`, message: `Clear "${f.label}" to see every tutor.`, clear: "filter" };
  return { title: "No tutors yet.", message: "Tutors appear here as soon as they sign up.", clear: null };
};
